import { Culture, cultureLabels } from "../context/VolunteersHighlightContext/constants";
import {
	UpdateVolunteersExperienceLocalizable,
	UpdateVolunteersExperienceRequest,
	VolunteersExperienceFormData,
	VolunteersExperienceRules,
} from "../context/VolunteersExperienceContext/constants";
import { toCrLf } from "../utils/lineBreaks";

const getCultureLabel = (culture: string) => cultureLabels[culture as Culture] ?? culture;

// The backend checks the trimmed text with its line breaks sent as \r\n.
const isTextInvalid = (value: string, maxLength: number) => {
	const trimmed = toCrLf(value.trim());
	return trimmed.length > maxLength || !VolunteersExperienceRules.textPattern.test(trimmed);
};

const invalidTextMessage = (field: string, maxLength: number) =>
	`${field} en fazla ${maxLength} karakter olmalı ve geçersiz karakter içermemelidir.`;

// undefined: not set (add) or unchanged (update); null: a filled value was cleared on the update form.
const validateDisplayOrder = (displayOrder?: number | null) => {
	if (displayOrder === undefined) {
		return null;
	}
	if (displayOrder === null) {
		return "Sıra numarası boş bırakılamaz.";
	}
	return Number.isInteger(displayOrder) && displayOrder >= 1 ? null : "Sıra numarası 1 veya daha büyük bir tam sayı olmalıdır.";
};

const validateName = (name: string) =>
	isTextInvalid(name, VolunteersExperienceRules.nameMaxLength) ? invalidTextMessage("Ad", VolunteersExperienceRules.nameMaxLength) : null;

const validateSurname = (surname: string) =>
	isTextInvalid(surname, VolunteersExperienceRules.surnameMaxLength)
		? invalidTextMessage("Soyad", VolunteersExperienceRules.surnameMaxLength)
		: null;

const validatePicture = (picture?: File | null) =>
	picture
	&& (picture.size > VolunteersExperienceRules.pictureMaxFileSize
		|| !(VolunteersExperienceRules.pictureMimeTypes as readonly string[]).includes(picture.type))
		? "Fotoğraf JPEG, JPG, PNG veya GIF formatında ve en fazla 5 MB olmalıdır."
		: null;

const validateDescription = (label: string, description: string) =>
	isTextInvalid(description, VolunteersExperienceRules.descriptionMaxLength)
		? invalidTextMessage(`${label} deneyim`, VolunteersExperienceRules.descriptionMaxLength)
		: null;

const validateOptionalText = (field: string, value: string | undefined, maxLength: number) =>
	value?.trim() && isTextInvalid(value, maxLength) ? invalidTextMessage(field, maxLength) : null;

const validateTitleAndCompany = (label: string, title?: string, company?: string) =>
	validateOptionalText(`${label} unvan`, title, VolunteersExperienceRules.titleMaxLength)
	?? validateOptionalText(`${label} şirket`, company, VolunteersExperienceRules.companyMaxLength);

// A culture that is created from scratch (add form, or a culture missing on the server) needs its experience text.
const validateCompleteLocalizable = (culture: string, description = "", title?: string, company?: string) => {
	const label = getCultureLabel(culture);

	if (!description.trim()) {
		return `Lütfen ${label} deneyim alanını doldurun.`;
	}

	return validateDescription(label, description) ?? validateTitleAndCompany(label, title, company);
};

// A changed field is checked like a new one; it cannot be emptied, because the backend keeps the stored value of a
// field that is sent empty (an optional title or company cannot be removed once saved).
const validateChangedText = (value: string | undefined, emptyMessage: string, validate: (value: string) => string | null) => {
	if (value === undefined) {
		return null;
	}
	return value.trim() ? validate(value) : emptyMessage;
};

// An existing culture is patched partially: only the fields that changed are present.
const validateChangedLocalizable = ({ culture, description, title, company }: UpdateVolunteersExperienceLocalizable) => {
	const label = getCultureLabel(culture);

	return (
		validateChangedText(description, `${label} deneyim boş bırakılamaz.`, value => validateDescription(label, value))
		?? validateChangedText(title, `${label} unvan kaydedildikten sonra boşaltılamaz.`, value => validateTitleAndCompany(label, value))
		?? validateChangedText(company, `${label} şirket kaydedildikten sonra boşaltılamaz.`, value => validateTitleAndCompany(label, undefined, value))
	);
};

export class VolunteersExperienceValidator {
	static readonly validateAddVolunteersExperience = (data: VolunteersExperienceFormData): string | null => {
		if (!data.name.trim()) {
			return "Lütfen ad alanını doldurun.";
		}
		if (!data.surname.trim()) {
			return "Lütfen soyad alanını doldurun.";
		}

		const generalError = validateName(data.name) ?? validateSurname(data.surname) ?? validateDisplayOrder(data.displayOrder) ?? validatePicture(data.picture);
		if (generalError) {
			return generalError;
		}

		for (const localizable of data.volunteersExperienceLocalizable) {
			const error = validateCompleteLocalizable(localizable.culture, localizable.description, localizable.title, localizable.company);
			if (error) {
				return error;
			}
		}

		return null;
	};

	// The request only carries changed fields, so an empty value here means the user cleared a filled field.
	static readonly validateUpdateVolunteersExperience = (data: UpdateVolunteersExperienceRequest): string | null => {
		const generalError =
			validateChangedText(data.name, "Ad boş bırakılamaz.", validateName)
			?? validateChangedText(data.surname, "Soyad boş bırakılamaz.", validateSurname)
			?? validateDisplayOrder(data.displayOrder)
			?? validatePicture(data.picture);
		if (generalError) {
			return generalError;
		}

		for (const localizable of data.volunteersExperienceLocalizable ?? []) {
			const error = localizable.volunteersExperienceLocalizableId
				? validateChangedLocalizable(localizable)
				: validateCompleteLocalizable(localizable.culture, localizable.description, localizable.title, localizable.company);
			if (error) {
				return error;
			}
		}

		return null;
	};
}
